import { useEffect, useRef, useState } from 'react'

/**
 * GoogleReviews — live Google reviews as an on-brand scrolling carousel.
 * Data: Featurable's free widget API (connected Greystone Google Business
 * Profile). Real reviews + the true aggregate (rating + count), auto-updating.
 * Cards are clickable → a modal shows the full review (Google Business reviews
 * have no per-review permalink, so we expand in-place + link to all reviews on
 * Google). Renders nothing until reviews load.
 *
 * Single-tenant for now; move the widget id to tenant config for tenant #2.
 */
const FEATURABLE_API =
  'https://featurable.com/api/v2/widgets/4aca4702-932a-4228-90fb-c1d37f6b72d5'

interface Review {
  id: string
  author: { name: string; avatarUrl?: string }
  text: string
  rating?: { value: number; max: number }
  publishedAt: string
}
interface Summary {
  rating: number
  reviewsCount: number
  writeAReviewUri?: string
}

function relativeDate(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const days = Math.floor((Date.now() - then) / 86_400_000)
  if (days < 1) return 'today'
  if (days < 7) return `${days}d ago`
  if (days < 30) return `${Math.floor(days / 7)}w ago`
  if (days < 365) return `${Math.floor(days / 30)}mo ago`
  return `${Math.floor(days / 365)}y ago`
}

function Stars({ n, max = 5 }: { n: number; max?: number }) {
  return (
    <div className="flex" aria-label={`${n} out of ${max} stars`}>
      {Array.from({ length: max }).map((_, i) => (
        <svg key={i} className="w-4 h-4" style={{ color: i < n ? '#F5B301' : '#E4E4DE' }} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M9.05 2.93c.3-.92 1.6-.92 1.9 0l1.36 4.18a1 1 0 00.95.69h4.4c.97 0 1.37 1.24.59 1.81l-3.56 2.59a1 1 0 00-.36 1.12l1.36 4.18c.3.92-.76 1.68-1.54 1.11l-3.56-2.59a1 1 0 00-1.18 0l-3.56 2.59c-.78.57-1.84-.19-1.54-1.11l1.36-4.18a1 1 0 00-.36-1.12L2.4 9.6c-.78-.57-.38-1.81.59-1.81h4.4a1 1 0 00.95-.69L9.05 2.93z" />
        </svg>
      ))}
    </div>
  )
}

function Avatar({ review, px = 40 }: { review: Review; px?: number }) {
  const style = { width: px, height: px }
  return review.author.avatarUrl ? (
    <img src={review.author.avatarUrl} alt="" style={style} className="rounded-full object-cover shrink-0" loading="lazy" referrerPolicy="no-referrer" />
  ) : (
    <div style={style} className="rounded-full bg-[#F4F4F0] grid place-items-center text-sm font-semibold text-ink shrink-0" aria-hidden="true">
      {review.author.name?.[0] ?? 'G'}
    </div>
  )
}

export default function GoogleReviews() {
  const [reviews, setReviews] = useState<Review[]>([])
  const [summary, setSummary] = useState<Summary | null>(null)
  const [selected, setSelected] = useState<Review | null>(null)
  const sectionRef = useRef<HTMLElement>(null)

  useEffect(() => {
    let cancelled = false
    fetch(FEATURABLE_API)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled || !d?.success) return
        const w = d.widget ?? {}
        setReviews((w.reviews ?? []).filter((r: Review) => r.text?.trim()))
        const s = w.gbpLocationSummary
        if (s) setSummary({ rating: s.rating, reviewsCount: s.reviewsCount, writeAReviewUri: s.writeAReviewUri })
      })
      .catch(() => {
        /* non-fatal — section hides if reviews can't load */
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    document.body.style.overflow = selected ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [selected])

  // When a visitor lands on /#reviews (e.g. the About-page reviews badge), the
  // carousel section only exists in the DOM once the Featurable fetch resolves —
  // so the browser's native hash scroll fires too early and leaves them at the
  // top. Scroll it into view ourselves the moment reviews are present, re-running
  // briefly to absorb async layout shifts from the strips loading above.
  useEffect(() => {
    if (reviews.length === 0) return
    if (typeof window === 'undefined' || window.location.hash !== '#reviews') return
    const scroll = () => sectionRef.current?.scrollIntoView({ block: 'start' })
    scroll()
    const t1 = setTimeout(scroll, 300)
    const t2 = setTimeout(scroll, 900)
    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
    }
  }, [reviews.length])

  if (reviews.length === 0) return null

  const loop = [...reviews, ...reviews]
  const rating = summary?.rating ?? 5
  const count = summary?.reviewsCount ?? reviews.length
  const seeAll = summary?.writeAReviewUri

  return (
    <section id="reviews" ref={sectionRef} className="py-16 lg:py-20 border-t border-[#E8E8E2] overflow-hidden scroll-mt-24">
      <div className="max-w-7xl mx-auto px-6 text-center mb-10">
        <div className="inline-flex items-center gap-2 mb-3">
          <Stars n={Math.round(rating)} />
          <span className="text-sm text-gs-text-soft">
            <span className="font-semibold text-ink">{rating.toFixed(1)}</span> on Google · {count} reviews
          </span>
        </div>
        <h2 className="font-heading text-3xl lg:text-4xl text-ink font-semibold">Trusted by Aussies across the country.</h2>
        <p className="text-gs-text-soft mt-3">Real, verified Google reviews. Tap any card to read the full review.</p>
      </div>

      <div className="marquee">
        <div className="marquee-track" style={{ ['--marquee-duration' as string]: `${Math.max(45, reviews.length * 9)}s` }}>
          {loop.map((rv, i) => (
            <button
              key={`${rv.id}-${i}`}
              type="button"
              onClick={() => setSelected(rv)}
              className="w-80 shrink-0 bg-white border border-[#E8E8E2] hover:border-[#C9C9C0] rounded-2xl p-6 mx-3 flex flex-col text-left cursor-pointer transition-colors"
            >
              <div className="flex items-center gap-3 mb-3">
                <Avatar review={rv} />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-ink truncate">{rv.author.name}</div>
                  <div className="text-xs text-gs-text-muted">{relativeDate(rv.publishedAt)}</div>
                </div>
                <svg className="w-5 h-5 ml-auto shrink-0" viewBox="0 0 24 24" aria-label="Google" role="img">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0012 23z" />
                  <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 010-4.2V7.06H2.18a11 11 0 000 9.88l3.66-2.84z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 002.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z" />
                </svg>
              </div>
              <Stars n={rv.rating?.value ?? 5} />
              <p className="text-sm text-gs-text-soft mt-3 leading-relaxed line-clamp-5">{rv.text}</p>
              <span className="text-xs font-medium text-ink mt-3">Read full review →</span>
            </button>
          ))}
        </div>
      </div>

      {seeAll && (
        <div className="text-center mt-10">
          <a href={seeAll} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-ink underline underline-offset-2 hover:opacity-80">
            See all {count} reviews on Google →
          </a>
        </div>
      )}

      {/* Full-review modal */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={(e) => { if (e.target === e.currentTarget) setSelected(null) }}
        >
          <div
            className="bg-white rounded-2xl shadow-card max-w-lg w-full p-8 max-h-[85vh] overflow-y-auto"
            role="dialog"
            aria-modal="true"
            aria-label="Full review"
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                <Avatar review={selected} px={48} />
                <div>
                  <div className="text-base font-semibold text-ink">{selected.author.name}</div>
                  <div className="text-xs text-gs-text-muted">{relativeDate(selected.publishedAt)} · via Google</div>
                </div>
              </div>
              <button onClick={() => setSelected(null)} aria-label="Close" className="text-gs-text-muted hover:text-ink text-xl leading-none">×</button>
            </div>
            <Stars n={selected.rating?.value ?? 5} />
            <p className="text-sm text-gs-text leading-relaxed mt-4 whitespace-pre-line">{selected.text}</p>
            {seeAll && (
              <a href={seeAll} target="_blank" rel="noopener noreferrer" className="inline-block mt-6 text-sm font-medium text-ink underline underline-offset-2 hover:opacity-80">
                See all {count} reviews on Google →
              </a>
            )}
          </div>
        </div>
      )}
    </section>
  )
}
